import { memo } from 'react';
import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ElapsedTimeDisplay, ProgressBar } from '@/components/common';
import type { DubbingProgress } from '@/types';
import type { MediaType } from '@/components/common/MediaUploader';

interface DubbingProgressTrackerProps {
  progress: DubbingProgress;
  /**
   * Which pipeline is running. The two pipelines emit DIFFERENT stages —
   * audio dubbing has no video to sync against or mux into, and instead
   * converts up front and exports a ZIP at the end. Rendering the video
   * stage list for an audio job made `findIndex` return -1 for the real
   * stages, so every step stayed grey while the job was actually running,
   * and "synchronization"/"muxing" never completed.
   */
  mediaType?: MediaType | null;
  className?: string;
}

interface PipelineStage {
  id: DubbingProgress['stage'];
  label: string;
  icon: string;
  description: string;
}

export const DubbingProgressTracker = memo(function DubbingProgressTracker({
  progress,
  mediaType = 'video',
  className = '',
}: DubbingProgressTrackerProps) {
  const { t } = useTranslation();

  const isAudio = mediaType === 'audio';

  // Keep these lists in step with the backend pipelines:
  //   video -> core_components/dubbing/orchestrator
  //   audio -> core_components/services/audio_upload_dubbing_service
  const stages: PipelineStage[] = isAudio
    ? [
        {
          id: 'conversion',
          label: t('dubbing.progress.stages.conversion'),
          icon: '🎛️',
          description: t('dubbing.progress.descriptions.conversion'),
        },
        {
          id: 'transcription',
          label: t('dubbing.progress.stages.transcription'),
          icon: '🎤',
          description: t('dubbing.progress.descriptions.transcriptionAudio'),
        },
        {
          id: 'translation',
          label: t('dubbing.progress.stages.translation'),
          icon: '🌐',
          description: t('dubbing.progress.descriptions.translation'),
        },
        {
          id: 'tts',
          label: t('dubbing.progress.stages.tts'),
          icon: '🔊',
          description: t('dubbing.progress.descriptions.tts'),
        },
        {
          id: 'export',
          label: t('dubbing.progress.stages.export'),
          icon: '📦',
          description: t('dubbing.progress.descriptions.export'),
        },
      ]
    : [
        {
          id: 'transcription',
          label: t('dubbing.progress.stages.transcription'),
          icon: '🎤',
          description: t('dubbing.progress.descriptions.transcription'),
        },
        {
          id: 'translation',
          label: t('dubbing.progress.stages.translation'),
          icon: '🌐',
          description: t('dubbing.progress.descriptions.translation'),
        },
        {
          id: 'tts',
          label: t('dubbing.progress.stages.tts'),
          icon: '🔊',
          description: t('dubbing.progress.descriptions.tts'),
        },
        {
          id: 'synchronization',
          label: t('dubbing.progress.stages.synchronization'),
          icon: '⏱️',
          description: t('dubbing.progress.descriptions.synchronization'),
        },
        {
          id: 'muxing',
          label: t('dubbing.progress.stages.muxing'),
          icon: '🎬',
          description: t('dubbing.progress.descriptions.muxing'),
        },
      ];

  // Which step the job died on. `progress.progress` is the last percentage the
  // backend reported, so it identifies how far the pipeline got even though
  // the stage field has been overwritten with 'error'.
  const failedAtIndex = Math.min(
    stages.length - 1,
    Math.floor((Math.max(0, progress.progress) / 100) * stages.length)
  );

  const getStageStatus = (
    stageId: DubbingProgress['stage']
  ): 'completed' | 'in_progress' | 'pending' | 'error' => {
    // When in 'pending' state (waiting to start), all stages are pending
    if (progress.stage === 'pending') {
      return 'pending';
    }

    if (progress.stage === 'error') {
      // 'error' is a status, not a pipeline step, so it is never in `stages`.
      // Looking it up there returned -1, which made every stage render as
      // "pending" on failure — no red marker anywhere. Use the last stage the
      // job actually reported to place the failure.
      const failedIndex = failedAtIndex;
      const stageIndex = stages.findIndex((s) => s.id === stageId);
      if (failedIndex < 0) return 'pending';
      if (stageIndex < failedIndex) return 'completed';
      if (stageIndex === failedIndex) return 'error';
      return 'pending';
    }

    if (progress.stage === 'completed') {
      return 'completed';
    }

    const currentIndex = stages.findIndex((s) => s.id === progress.stage);
    const stageIndex = stages.findIndex((s) => s.id === stageId);

    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'in_progress';
    return 'pending';
  };

  const getStageIcon = (
    _stageId: DubbingProgress['stage'],
    status: 'completed' | 'in_progress' | 'pending' | 'error'
  ) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Circle className="w-5 h-5 text-gray-300" />;
    }
  };

  const formatETA = (seconds: number): string => {
    if (seconds < 60) {
      return t('dubbing.progress.etaSeconds', {
        seconds,
        defaultValue: '{{seconds}}s',
      });
    }
    const minutes = Math.ceil(seconds / 60);
    return t('dubbing.progress.etaMinutes', {
      minutes,
      defaultValue: '{{minutes}}m',
    });
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('dubbing.progress.title', { defaultValue: 'Dubbing Progress' })}
        </h3>
        <p className="text-sm text-gray-600">{progress.message}</p>
      </div>

      {/* Pipeline stages */}
      <div className="space-y-3 mb-6">
        {stages.map((stage, index) => {
          const status = getStageStatus(stage.id);
          const isActive = status === 'in_progress';

          return (
            <div key={stage.id} className="relative">
              {/* Connection line */}
              {index < stages.length - 1 && (
                <div
                  className={`absolute left-[10px] top-8 w-0.5 h-6 ${
                    status === 'completed' ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                />
              )}

              {/* Stage row */}
              <div
                className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  isActive ? 'bg-blue-50' : ''
                }`}
              >
                {/* Icon */}
                <div className="flex-shrink-0">{getStageIcon(stage.id, status)}</div>

                {/* Stage info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{stage.icon}</span>
                    <p
                      className={`text-sm font-medium ${
                        isActive
                          ? 'text-blue-900'
                          : status === 'completed'
                            ? 'text-gray-900'
                            : status === 'error'
                              ? 'text-red-900'
                              : 'text-gray-500'
                      }`}
                    >
                      {stage.label}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{stage.description}</p>
                </div>

                {/* Progress percentage for active stage */}
                {isActive && (
                  <div className="flex-shrink-0">
                    <span className="text-sm font-semibold text-blue-600">
                      {Math.round(progress.progress)}%
                    </span>
                  </div>
                )}

                {/* Completed checkmark */}
                {status === 'completed' && (
                  <div className="flex-shrink-0">
                    <span className="text-xs text-green-600 font-medium">
                      {t('dubbing.progress.stageDone', { defaultValue: 'Completed' })}
                    </span>
                  </div>
                )}

                {/* Error indicator */}
                {status === 'error' && (
                  <div className="flex-shrink-0">
                    <span className="text-xs text-red-600 font-medium">
                      {t('dubbing.progress.error', { defaultValue: 'Error' })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
          <span>
            {t('dubbing.progress.overallProgress', {
              defaultValue: 'Overall Progress',
            })}
          </span>
          <span>{Math.round(progress.progress)}%</span>
        </div>
        <ProgressBar
          percent={progress.progress}
          color={
            progress.stage === 'error'
              ? 'bg-red-600'
              : progress.stage === 'completed'
                ? 'bg-green-600'
                : 'bg-blue-600'
          }
        />
      </div>

      {/* Footer with elapsed time and steps */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-gray-500 pt-4 border-t border-gray-200">
        <div>
          {progress.current_step !== undefined && progress.total_steps && (
            <span>
              {t('dubbing.progress.step', {
                current: progress.current_step,
                total: progress.total_steps,
                defaultValue: 'Step {{current}} of {{total}}',
              })}
            </span>
          )}
        </div>
        <div>
          {progress.stage !== 'completed' && progress.stage !== 'error' && (
            <ElapsedTimeDisplay
              startedAt={progress.started_at ?? null}
              serverElapsedSeconds={progress.elapsed_seconds ?? null}
              estimatedRemainingSeconds={progress.estimated_remaining_seconds ?? null}
              size="sm"
            />
          )}
          {/* Fallback to legacy ETA if new timing fields not available */}
          {!progress.started_at &&
            progress.eta &&
            progress.eta > 0 &&
            progress.stage !== 'completed' && (
              <span>
                {t('dubbing.progress.estimated', {
                  defaultValue: 'Estimated:',
                })}{' '}
                {formatETA(progress.eta)}
              </span>
            )}
        </div>
      </div>

      {/* Error message */}
      {progress.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{progress.error}</p>
        </div>
      )}

      {/* Success message */}
      {progress.stage === 'completed' && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">
            {t('dubbing.progress.successMessage', {
              defaultValue: 'Dubbing completed successfully!',
            })}
          </p>
        </div>
      )}
    </div>
  );
});
